import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { InputText } from "primereact/inputtext";
import { Sidebar } from "primereact/sidebar";
import { Dropdown } from "primereact/dropdown";
import InputComponent from "./Input";
import CheckboxComponent from "./Checkbox";
import DropdownComponent from "./Dropdown";
import RadioComponent from "./RadioButtons";
import DateComponent from "./Date";
import TextareaComponent from "./Textarea";
import FileUploadComponent from "./FileUpload";
import InputSwitchComponent from "./InputSwitch";
import MapCoordinatesComponent from "../../GenerateForms/MapCoordinates";
import MultiSelectComponent from "./Multiselect";

const GenerateFieldsDialog = ({
  createFieldDialogVisible,
  setCreateFieldDialogVisible,
  onSaveField,
  existingFields,
  onGroupsUpdate,
}) => {
  const toast = useRef(null);
  const [visibleRight, setVisibleRight] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [buttonName, setButtonName] = useState("");
  const [fieldName, setFieldName] = useState("");
  const [key, setKey] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupTitleDialogVisible, setGroupTitleDialogVisible] = useState(false);
  const [newGroupTitle, setNewGroupTitle] = useState("");
  const [newGroupFieldsPerRow, setNewGroupFieldsPerRow] = useState("");
  const [editGroupDialogVisible, setEditGroupDialogVisible] = useState(false);
  const [editGroupTitle, setEditGroupTitle] = useState("");
  const [originalGroupTitle, setOriginalGroupTitle] = useState("");

  useEffect(() => {
    onGroupsUpdate(groups);
  }, [groups, onGroupsUpdate]);

  const handleEditGroup = (groupTitle) => {
    const group = groups.find((g) => g.title === groupTitle);
    if (!group) return;

    // Store the original title when we start editing
    setOriginalGroupTitle(groupTitle);
    setEditGroupTitle(groupTitle);
    setNewGroupFieldsPerRow(group.fieldsPerRow);
    setEditGroupDialogVisible(true);
  };

  const handleSaveEditGroup = () => {
    const trimmedTitle = editGroupTitle.trim();
    const fieldsPerRow = parseInt(newGroupFieldsPerRow, 10);

    if (trimmedTitle === "") {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Group title cannot be empty",
        life: 3000,
      });
      return;
    }

    if (isNaN(fieldsPerRow) || fieldsPerRow < 1) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail:
          "Number of Fields Per Row must be a whole number greater than or equal to 1",
        life: 3000,
      });
      return;
    }

    // Check if new name conflicts with existing groups
    if (
      trimmedTitle !== originalGroupTitle &&
      groups.some(
        (group) => group.title.toLowerCase() === trimmedTitle.toLowerCase()
      )
    ) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Group name must be unique",
        life: 3000,
      });
      return;
    }

    // Update fields that belong to this group
    const updatedFields = existingFields.map((field) => {
      if (field.group === originalGroupTitle) {
        return { ...field, group: trimmedTitle, fieldsPerRow };
      }
      return field;
    });

    // Call onSaveField with the updated fields
    onSaveField(updatedFields, true);

    // Update the groups array
    setGroups(
      groups.map((g) =>
        g.title === originalGroupTitle
          ? { title: trimmedTitle, fieldsPerRow }
          : g
      )
    );

    // Update selected group if necessary
    if (selectedGroup === originalGroupTitle) {
      setSelectedGroup(trimmedTitle);
    }

    toast.current.show({
      severity: "success",
      summary: "Success",
      detail: "Group updated successfully",
      life: 3000,
    });

    // Reset states
    setEditGroupDialogVisible(false);
    setOriginalGroupTitle("");
    setEditGroupTitle("");
    setNewGroupFieldsPerRow("");
  };
  const addNewGroup = () => {
    setGroupTitleDialogVisible(true);
  };

  const handleAddGroup = () => {
    const trimmedTitle = newGroupTitle.trim();
    const fieldsPerRow = parseInt(newGroupFieldsPerRow, 10);
    if (trimmedTitle === "") {
      if (toast.current) {
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail: "Group title cannot be empty",
          life: 3000,
        });
      }
      return;
    }
    if (isNaN(fieldsPerRow) || fieldsPerRow < 1) {
      if (toast.current) {
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail:
            "Number of Fields Per Row must be a whole number greater than or equal to 1",
          life: 3000,
        });
      }
      return;
    }
    // Check if group name already exists
    if (
      groups.some(
        (group) => group.title.toLowerCase() === trimmedTitle.toLowerCase()
      )
    ) {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Group name must be unique",
          life: 3000,
        });
      }
      return;
    }

    // Only close dialog and update state if validation passes
    setGroups([...groups, { title: trimmedTitle, fieldsPerRow }]);
    setSelectedGroup(trimmedTitle);
    setNewGroupTitle("");
    setNewGroupFieldsPerRow(1);
    setGroupTitleDialogVisible(false);
  };

  const styles = {
    dropdownItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
      padding: "0.5rem 1rem",
      position: "relative",
    },
    buttonContainer: {
      display: "flex",
      alignItems: "center",
      marginLeft: "auto",
    },
    removeButton: {
      padding: "0.25rem",
    },
    editButton: {
      padding: "0.25rem",
    },
  };

  const removeGroup = (groupToRemove, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if group is used by any fields
    const isGroupUsed = existingFields.some(
      (field) => field.group === groupToRemove
    );

    if (isGroupUsed) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Cannot remove group that contains fields",
        life: 3000,
      });
    } else {
      setGroups(groups.filter((g) => g.title !== groupToRemove));
      if (selectedGroup === groupToRemove) {
        setSelectedGroup(null);
      }
    }
  };
  const handleButtonClick = (component, name) => {
    if (!fieldName) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter the field label before selecting a component.",
        life: 3000,
      });
      return;
    }
    if (!key) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "Please enter the key before selecting a component.",
        life: 3000,
      });
      return;
    }

    if (existingFields.some((field) => field.name === fieldName)) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Field name must be unique. Please enter a different name.",
        life: 3000,
      });
      return;
    }

    if (existingFields.some((field) => field.key === key)) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Key must be unique. Please enter a different key.",
        life: 3000,
      });
      return;
    }

    setVisibleRight(true);
    setSelectedComponent(
      React.cloneElement(component, {
        fieldName,
        key,
        group: selectedGroup,
        onSave: handleSaveField,
        closeSidebar,
      })
    );
    setButtonName(name);
  };

  const closeSidebar = () => {
    setVisibleRight(false);
  };

  const handleSaveField = (fieldData) => {
    fieldData.name = fieldName;
    fieldData.key = key;
    fieldData.group = selectedGroup;

    if (selectedGroup) {
      const group = groups.find((g) => g.title === selectedGroup);
      fieldData.fieldsPerRow = group ? group.fieldsPerRow : 1;
    } else {
      fieldData.fieldsPerRow = 1;
    }

    onSaveField(fieldData);
    setVisibleRight(false);
  };

  useEffect(() => {
    setFieldName("");
    setKey("");
    setSelectedGroup(null);
  }, [createFieldDialogVisible]);

  const handleGroupFieldsPerRowChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 1) {
      if (toast.current) {
        toast.current.show({
          severity: "warn",
          summary: "Warning",
          detail:
            "Number of Fields Per Row must be a whole number greater than or equal to 1",
          life: 3000,
        });
      }
      setNewGroupFieldsPerRow(1); // Reset to 1 if value is invalid
    } else {
      setNewGroupFieldsPerRow(value);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        visible={createFieldDialogVisible}
        style={{
          width: window.innerWidth > 768 ? "80vw" : "95vw",
          maxHeight: window.innerWidth > 768 ? "90vh" : "95vh",
          fontFamily: "poppins",
        }}
        className="generate-fields-dialog"
        modal
        onHide={() => setCreateFieldDialogVisible(false)}
      >
        <div className="p-field">
          <label htmlFor="name" className="block text-900 font-medium mb-2">
            Key
          </label>
          <InputText
            id="name"
            placeholder="Enter Key"
            value={fieldName}
            onChange={(e) => setFieldName(e.target.value)}
            className="w-full mb-3"
          />
        </div>
        <div className="p-field">
          <label htmlFor="name" className="block text-900 font-medium mb-2">
            Field Label
          </label>
          <InputText
            id="name"
            placeholder="Enter Field Label"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="w-full mb-3"
          />
        </div>
        <div className="p-field">
          <label htmlFor="group" className="block text-900 font-medium mb-2">
            Field Group
          </label>
          <div className="flex gap-2" style={{ width: "100%" }}>
            <Dropdown
              filter
              id="group"
              value={selectedGroup}
              options={
                groups.length > 0
                  ? groups
                      .filter((g) => g !== undefined && g !== null)
                      .map((g) => ({
                        label: g.title,
                        value: g.title,
                      }))
                  : []
              }
              onChange={(e) => setSelectedGroup(e.value)}
              placeholder="Select a group"
              className="w-full mb-3"
              emptyMessage="No groups available"
              showClear
              itemTemplate={(option) => (
                <div style={styles.dropdownItem}>
                  <span>{option.label}</span>
                  {option.value && (
                    <div style={styles.buttonContainer}>
                      <Button
                        icon="pi pi-pencil"
                        className="p-button-text p-button-rounded p-button-warning"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditGroup(option.value);
                        }}
                        style={styles.editButton}
                      />
                      <Button
                        icon="pi pi-times"
                        className="p-button-text p-button-rounded p-button-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeGroup(option.value, e);
                        }}
                        style={styles.removeButton}
                      />
                    </div>
                  )}
                </div>
              )}
              valueTemplate={(option) => {
                return option ? `${option.value}` : "Select a group";
              }}
            />
            <Button
              icon="pi pi-plus"
              className="p-button-outlined align-self-start"
              style={{ backgroundColor: "#d97a07", color: "#fff" }}
              onClick={addNewGroup}
              tooltip="Add new group"
            />
          </div>
        </div>

        <div className="p-d-flex p-jc-between p-ai-center p-mb-3 mb-3">
          <label htmlFor="name" className="block text-900 font-medium mb-2">
            Field Type
          </label>
          <Button
            label="Input"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
            }}
            onClick={() => handleButtonClick(<InputComponent />, "Input")}
          />
          <Button
            label="Checkbox"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() => handleButtonClick(<CheckboxComponent />, "Checkbox")}
          />
          <Button
            label="Dropdown"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() => handleButtonClick(<DropdownComponent />, "Dropdown")}
          />
          <Button
            label="MultiSelect"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() =>
              handleButtonClick(<MultiSelectComponent />, "MultiSelect")
            }
          />
          <Button
            label="Radio Buttons"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() =>
              handleButtonClick(<RadioComponent />, "Radio Buttons")
            }
          />
          <Button
            label="Textarea"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() => handleButtonClick(<TextareaComponent />, "Textarea")}
          />
          <Button
            label="Switch"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() =>
              handleButtonClick(<InputSwitchComponent />, "Switch")
            }
          />
          <Button
            label="Date"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() => handleButtonClick(<DateComponent />, "Date")}
          />
          <Button
            label="File Upload"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() =>
              handleButtonClick(<FileUploadComponent />, "File Upload")
            }
          />
          <Button
            label="Map Coordinates"
            className="p-button p-component p-button-text mt-2 mb-2"
            style={{
              backgroundColor: "#0D2E4D",
              color: "#fff",
              border: "none",
              marginLeft: "0.5rem",
            }}
            onClick={() =>
              handleButtonClick(<MapCoordinatesComponent />, "Map Coordinates")
            }
          />
        </div>
        <Sidebar
          visible={visibleRight}
          position="right"
          onHide={() => setVisibleRight(false)}
        >
          <h2>{buttonName}</h2>
          {selectedComponent}
        </Sidebar>
      </Dialog>
      <Dialog
        visible={groupTitleDialogVisible}
        style={{
          width: window.innerWidth > 768 ? "30vw" : "95vw",
          fontFamily: "poppins",
        }}
        modal
        onHide={() => setGroupTitleDialogVisible(false)}
        header="Add New Group"
      >
        <div className="p-field">
          <label
            htmlFor="groupTitle"
            className="block text-900 font-medium mb-2"
          >
            Group Title
          </label>
          <InputText
            id="groupTitle"
            placeholder="Enter Group Title"
            value={newGroupTitle}
            onChange={(e) => setNewGroupTitle(e.target.value)}
            className="w-full mb-3"
          />
        </div>
        <div className="p-field">
          <label
            htmlFor="groupFieldsPerRow"
            className="block text-900 font-medium mb-2"
          >
            Number of Fields Per Row
          </label>
          <InputText
            id="groupFieldsPerRow"
            placeholder="Enter Number of Fields Per Row"
            type="number"
            value={newGroupFieldsPerRow}
            onChange={handleGroupFieldsPerRowChange}
            className="w-full mb-3"
          />
        </div>
        <div className="p-d-flex p-jc-end">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setGroupTitleDialogVisible(false)}
          />
          <Button
            label="Save"
            className="p-button p-component p-button-primary"
            onClick={handleAddGroup}
          />
        </div>
      </Dialog>
      <Dialog
        visible={editGroupDialogVisible}
        style={{
          width: window.innerWidth > 768 ? "30vw" : "95vw",
          fontFamily: "poppins",
        }}
        header="Edit Group"
        modal
        onHide={() => setEditGroupDialogVisible(false)}
      >
        <div className="p-field">
          <label
            htmlFor="editGroupTitle"
            className="block text-900 font-medium mb-2"
          >
            Edit Group Title
          </label>
          <InputText
            id="editGroupTitle"
            placeholder="Enter Group Title"
            value={editGroupTitle}
            onChange={(e) => setEditGroupTitle(e.target.value)}
            className="w-full mb-3"
          />
        </div>
        <div className="p-field">
          <label
            htmlFor="editGroupFieldsPerRow"
            className="block text-900 font-medium mb-2"
          >
            Number of Fields Per Row
          </label>
          <InputText
            id="editGroupFieldsPerRow"
            placeholder="Enter Number of Fields Per Row"
            type="number"
            value={newGroupFieldsPerRow}
            onChange={(e) => setNewGroupFieldsPerRow(e.target.value)}
            className="w-full mb-3"
          />
        </div>
        <div className="p-d-flex p-jc-end">
          <Button
            label="Cancel"
            className="p-button-text"
            onClick={() => setEditGroupDialogVisible(false)}
          />
          <Button
            label="Save"
            className="p-button p-component p-button-primary"
            onClick={handleSaveEditGroup}
          />
        </div>
      </Dialog>
    </>
  );
};

export default GenerateFieldsDialog;
